import Modal from "@via/components/modal";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useChangesDetect } from "@via/hooks/useChangesDetect";
import {
  useCreateOrUpdatePipeline,
  usePipelineIsUsed,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { PipelineOperations } from "@via/pages/projects/pipelines/workflow/utils/constants.tsx";
import { isPipelineExternal } from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  extractSha,
  filterEmptyFolders,
  packDraftForSave,
  packScriptConfig,
} from "@via/pages/projects/pipelines/workflow/utils/helpers.tsx";
import { ModalSize, USER_ROLES } from "@via/utils/constants";
import { classNames } from "@via/utils/helpers.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useEffect, useState } from "react";

const isUsedByUserMessage =
  "Some processes in this pipeline are used only in your runs. So you can either save your changes on same version or create a new revision.";
const isUsedByOthersAdminMessage =
  "Some processes within the pipeline are in use by others.";
const isUsedByOthersUserMessage =
  "Some processes within the pipeline are in use by others. You can save your changes by creating a new revision.";

const SavePipelineModal = ({
  open,
  setOpen,
  draft,
  scripts,
  userRole,
  handleGoToCreateRevision,
}) => {
  const [payload, setPayload] = useState<any>(null);
  const [revComment, setRevComment] = useState("");
  const [message, showMessage] = useState("");
  const [sha, setSha] = useState<string | null>(null);
  const [visibleRevComment, setVisibleRevComment] = useState("");

  const { setPipelineDraft, setScriptPipeConfig } = useGlobal();
  const { resetChangeCount } = useChangesDetect();

  const {
    mutate: checkPipelineIsUsed,
    data: isPipelineUsedData,
    isPending: isPipelineUsedLoading,
  } = usePipelineIsUsed();

  const {
    mutate: savePipeline,
    isPending: isPipelineSaveLoading,
    isSuccess: isPipelineSaveSuccess,
  } = useCreateOrUpdatePipeline();

  useEffect(() => {
    if (isPipelineUsedData?.isUsed) {
      const { usedByRequesterUser, usedByOtherUsers } =
        isPipelineUsedData.isUsed;
      if (usedByRequesterUser) {
        showMessage(isUsedByUserMessage);
      } else if (usedByOtherUsers && userRole === USER_ROLES.ADMIN) {
        showMessage(isUsedByOthersAdminMessage);
      } else if (usedByOtherUsers && userRole === USER_ROLES.USER) {
        showMessage(isUsedByOthersUserMessage);
      }
    }
  }, [isPipelineUsedData]);

  useEffect(() => {
    if (!draft || !scripts) return;

    const filteredEmptyFolders = filterEmptyFolders(scripts);
    const config = packScriptConfig(filteredEmptyFolders);
    const formatted = packDraftForSave(draft, config, PipelineOperations.Save);
    setPayload(formatted);
    const fullComment = (formatted?.revComment as string) || "";

    if (isPipelineExternal(draft.pipelineType)) {
      const extractedSha = extractSha(fullComment);
      setSha(extractedSha);
      setVisibleRevComment(
        fullComment.replace(/\s*\([\da-f]{7,40}\)/i, "").trim()
      );
    } else {
      setVisibleRevComment(fullComment);
    }

    setRevComment(fullComment); // Keep full comment with SHA for reference
    const { pipelineRevUUId, pipelineUUId, ...bodyPayload } = formatted;
    checkPipelineIsUsed({
      payload: bodyPayload,
    });
  }, [draft, scripts]);

  useEffect(() => {
    if (isPipelineSaveSuccess) {
      setOpen(false);
      setPipelineDraft(prevData => ({ ...prevData, revComment }));

      const filteredEmptyFolders = filterEmptyFolders(scripts);
      setScriptPipeConfig(filteredEmptyFolders);

      resetChangeCount();
    }
  }, [isPipelineSaveSuccess]);

  const handleConfirmModal = async (): Promise<void> => {
    let finalComment = visibleRevComment.trim();

    if (isPipelineExternal(draft.pipelineType) && sha) {
      finalComment = `(${sha}) ${finalComment}`;
    }

    const submission = { ...payload, revComment: finalComment };
    const { pipelineRevUUId, pipelineUUId, ...bodyPayload } = submission;
    savePipeline(bodyPayload);
  };

  const used = isPipelineUsedData?.isUsed;
  const usedBySelf = used?.usedByRequesterUser;
  const usedByOthers = used?.usedByOtherUsers;
  const isAdmin = userRole === USER_ROLES.ADMIN;

  const showSave =
    (!usedBySelf && !usedByOthers) || // used by no one
    (usedBySelf && !usedByOthers) || // only self
    (usedByOthers && isAdmin); // used by others, admin only

  const saveColor =
    !usedBySelf && !usedByOthers
      ? "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
      : usedBySelf && !usedByOthers
        ? "bg-yellow-600 hover:bg-yellow-500 focus-visible:outline-yellow-600"
        : "bg-red-600 hover:bg-red-500 focus-visible:outline-red-600";

  const showGoToCreateRevision = (usedBySelf && !usedByOthers) || usedByOthers;

  return (
    <Modal
      isLoading={isPipelineUsedLoading}
      open={open}
      setOpen={setOpen}
      title="Save Pipeline"
      size={ModalSize.Large}
      hasCancelButton={false}
      isConfirmLoading={isPipelineSaveLoading}>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleConfirmModal();
        }}>
        <div className="mt-0 max-w-[650px]">
          <span className="text-sm text-gray-600">{message}</span>
          <div className="my-6 flex mt-4 mb-2 w-full flex-col">
            <label className="mt-4 mb-2 block text-sm font-medium text-gray-900">
              Comment
            </label>
            <div className="w-full">
              <textarea
                rows={4}
                value={visibleRevComment}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder={"Enter a Revision Comment"}
                onChange={e => setVisibleRevComment(e.target.value)}
              />
            </div>
            {isPipelineExternal(draft.pipelineType) &&
              extractSha(draft?.revComment) && (
                <label className="mt-4 block text-sm font-medium text-gray-900">
                  SHA:{" "}
                  <span className="font-normal">
                    {extractSha(draft?.revComment)}
                  </span>
                </label>
              )}
          </div>
          <div className="flex w-full gap-2 justify-end">
            {showSave && (
              <button
                type="submit"
                className={classNames(
                  "inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
                  saveColor
                )}
                disabled={isPipelineUsedLoading || isPipelineSaveLoading}>
                <Spinner
                  loading={isPipelineUsedLoading || isPipelineSaveLoading}>
                  Save
                </Spinner>
              </button>
            )}

            {showGoToCreateRevision && (
              <button
                type="button"
                onClick={handleGoToCreateRevision}
                className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                Go to Create Revision
              </button>
            )}

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm text-white shadow-sm duration-200 ease-in-out bg-gray-600 hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">
              Cancel
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default SavePipelineModal;
